"use client";

import { useEffect } from "react";
import type { LearningJourney } from "@/lib/learning-journey/journey";
import type { RangeRunner } from "@/lib/learning-journey/runtime/range-runner";

/**
 * `←` goes back one **sub-step** of the current unit, mirroring the « Retour »
 * button `MiniSteps` renders.
 *
 * One sub-step and not one segment of the stepper. The five segments fold
 * nine states, so the shortcut used to leave the two-hand practice on the
 * right hand's *grip*: two steps behind, with the right hand's own run and
 * the two hands together to be played again before the player was back where
 * they started. The FSM walks the states one at a time and knows which ones
 * this unit has — see `PhraseFSM.previousState`.
 *
 * No-op when there is nothing behind the current step, and ignored while
 * typing in an input / textarea / contentEditable surface so the shortcut
 * never hijacks text entry.
 *
 * Désarmé pendant une passe : `←` sortait de S4 en plein milieu, transport
 * en marche. L'état de la passe est lu sur le `RangeRunner` au moment de la
 * touche — il n'est pas réactif, mais c'est le seul instant qui compte.
 */
export function useStepBackShortcut(
  canStepBack: boolean,
  journey: LearningJourney,
  rangeRunner: RangeRunner | null,
): void {
  useEffect(() => {
    if (!canStepBack) {
      return;
    }

    const handler = (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft") {
        return;
      }

      if (isEditableTarget(e.target)) {
        return;
      }

      if (rangeRunner?.isRunning()) {
        return;
      }

      e.preventDefault();
      journey.stepBack();
    };

    document.addEventListener("keydown", handler);

    return () => {
      document.removeEventListener("keydown", handler);
    };
  }, [canStepBack, journey, rangeRunner]);
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tag = target.tagName;

  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
    return true;
  }

  return target.isContentEditable;
}
